import { config } from '../config';
import { captureException } from '../lib/sentry';

// ApiError is thrown by every non-2xx response. `code` is the server's opaque
// error string (e.g. 'slug_taken'); UI maps it to a translated message. `data`
// is the full parsed JSON body (when present) — endpoints that return
// structured error detail (e.g. members import validation_errors) rely on it.
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly data: unknown;

  constructor(status: number, code: string, data?: unknown, message?: string) {
    super(message ?? code);
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

// Module-level callback invoked once when the server reports a 401 the SPA
// can't recover from (invalid_token, missing_token, csrf_mismatch). main.tsx
// installs a handler that signs the user out + redirects to Zitadel.
// Module-level (not React state) so non-React callers — global error handlers,
// EventSource listeners — share the same dispatch path.
let unauthorizedHandler: (() => void) | null = null;
let unauthorizedFired = false;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
  unauthorizedFired = false;
}

function dispatchUnauthorized(): void {
  if (unauthorizedFired) return;
  unauthorizedFired = true;
  // Defer to a microtask so the throwing apiFetch call settles before the
  // handler tears down auth state — otherwise react-query sees the rejection
  // mid-redirect and surfaces a flash of error UI.
  queueMicrotask(() => {
    try {
      unauthorizedHandler?.();
    } catch {
      // Swallow — the handler is best-effort; the page is about to unload.
    }
  });
}

// SESSION_COOKIE_BRIDGE — apiFetch supports BOTH transport modes:
//   1. Bearer header (existing): caller passes opts.token (typically from
//      useAuth().user?.access_token). Server validates the JWT directly.
//   2. Cookie session: the apex `.sportadmin.io` cookie set by
//      POST /api/v1/auth/session. credentials:'include' carries it; on
//      mutating methods we also echo the sa_csrf cookie as X-CSRF-Token
//      so the server's synchronizer-token CSRF check accepts the call.
// Both are sent on every request; the server picks Bearer first and
// only falls back to the cookie if no Bearer is present. This means
// the existing token-threading codepath keeps working unchanged, AND
// the universal app at app.sportadmin.io picks up the cookie inside
// the Settings → App iframe without any per-call wiring.
export async function apiFetch<T>(
  path: string,
  opts: { method?: string; body?: unknown; token?: string | null } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  if (opts.body !== undefined && !isFormData) {
    // For FormData the browser sets Content-Type with its own
    // multipart boundary — we MUST NOT set it manually.
    headers['Content-Type'] = 'application/json';
  }
  if (opts.token) {
    headers['Authorization'] = `Bearer ${opts.token}`;
  }
  const method = opts.method ?? 'GET';
  // Always echo the CSRF cookie value on mutating methods, even when a
  // Bearer is present. The server short-circuits the CSRF check when
  // auth came from Bearer, so the redundant header is harmless; sending
  // it unconditionally keeps this module from having to know whether
  // the server is using the cookie or the header for THIS request.
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = readCSRFToken();
    if (csrf) {
      headers['X-CSRF-Token'] = csrf;
    }
  }

  const bodyOut: BodyInit | undefined =
    opts.body === undefined
      ? undefined
      : isFormData
        ? (opts.body as FormData)
        : JSON.stringify(opts.body);

  const res = await fetch(`${config.apiURL}${path}`, {
    method,
    headers,
    // credentials:'include' so the apex sa_session cookie travels on the
    // cross-origin go.sportadmin.io → api.sportadmin.io call. The matching
    // Access-Control-Allow-Credentials: true header is already set by the
    // server's corsMiddleware against the SPA origin.
    credentials: 'include',
    // Conditional spread: omit `body` entirely when undefined.
    // exactOptionalPropertyTypes rejects an explicit `undefined` here.
    ...(bodyOut !== undefined ? { body: bodyOut } : {}),
  });

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  const parsed = text ? safeParse(text) : null;

  if (!res.ok) {
    const code =
      (parsed && typeof parsed === 'object' && 'error' in parsed && typeof parsed.error === 'string'
        ? parsed.error
        : null) ?? `http_${res.status}`;
    const apiErr = new ApiError(res.status, code, parsed);
    // 401 = the server rejected our credentials. The auth middleware emits
    // missing_token / invalid_token / csrf_mismatch; any of them means the
    // local session is unusable and the SPA must re-authenticate. Fire the
    // unauthorized hook so main.tsx tears down OIDC state and bounces to
    // Zitadel. Idempotent — only the first 401 in a session triggers.
    if (res.status === 401) {
      dispatchUnauthorized();
    }
    // 5xx → server-side bug or outage. Capture so we see backend failures
    // from the frontend's perspective (the backend captures its own 5xx
    // separately via internal/sentry HTTPMiddleware, but a network blip,
    // CDN error, or proxy issue won't reach that path). 4xx is expected
    // user-input territory, so we let those propagate to caller-side
    // handling without spamming Sentry.
    if (res.status >= 500) {
      captureException(apiErr);
    }
    throw apiErr;
  }

  return parsed as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// readCSRFToken pulls the sa_csrf value out of document.cookie. The cookie
// is NOT HttpOnly so JS can read it; the synchronizer-token check on the
// server compares it against the X-CSRF-Token header we set above. Returns
// null in non-browser contexts (SSR / tests) and when the cookie is absent
// (pre-login, after logout) — in those cases mutating requests fall back
// to the Bearer-only path the server has always supported.
export function readCSRFToken(): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = 'sa_csrf=';
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length);
    }
  }
  return null;
}
